import { useState } from 'react'
import { DataGrid, GridToolbar, GridPaginationModel, GridColDef } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CardProps,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography
} from '@mui/material'
import {
  Device,
  PrometheusAlert,
  RuuviTag,
  RuuviTagBattery,
  RuuviTagCondition,
  Sensor,
  ValveSensor
} from '../types'
import {
  getAlertTimestamp,
  getUniqueAlerts,
  translateAlertRuleName,
  translateAlertSeverity,
  getAlertValue,
  getAlertThresholdValue,
  getAlertValueBySensor,
  getAlertValueByValveSensor,
  getAlertValueByRuuviTagCondition,
  getAlertValueByRuuviTagBattery,
  formatAlertValue,
  formatAlertThresholdValue,
  formatAlertValueBySensor,
  formatAlertValueByRuuviTagBattery,
  formatAlertValueByRuuviTagCondition,
  formatAlertValueByValveSensor,
  isSensorAlert,
  isValveSensorAlert,
  isRuuviTagConditionAlert,
  isRuuviTagBatteryAlert
} from '../utils/alert'
import LoadingState from './LoadingState'
import { humanizeTimestamp } from '../utils/date'
import AlertSeverityChip from './AlertSeverityChip'
import BlinkingComponent from './BlinkingComponent'
import AlertSummary from './AlertSummary'
import AlertRuleLabel from './AlertRuleLabel'

interface ActiveAlertsCardProps extends Omit<CardProps, 'children'> {
  device?: Device | null
  sensor?: Sensor | null
  valveSensor?: ValveSensor | null
  ruuviTag?: RuuviTag | null
  ruuviTagCondition?: RuuviTagCondition | null
  ruuviTagBattery?: RuuviTagBattery | null
  alerts: PrometheusAlert[]
  loading?: boolean
}

export default function ActiveAlertsCard({
  device,
  sensor,
  valveSensor,
  ruuviTag,
  ruuviTagCondition,
  ruuviTagBattery,
  alerts,
  loading = false
}: ActiveAlertsCardProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The pagination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 100,
    page: 0
  })

  const [translationKey, setTranslationKey] = useState<'summary' | 'eval'>('summary')

  /**
   * The columns.
   */
  const columns: GridColDef<PrometheusAlert>[] = [
    {
      field: 'activeAt',
      headerName: t('alert:alertingStartedAt'),
      type: 'date',
      width: 200,
      editable: false,
      valueGetter: (_, row) => getAlertTimestamp(row),
      valueFormatter: (value) => {
        if (value) {
          return humanizeTimestamp(value)
        } else {
          return null
        }
      },
      renderCell: ({ row, formattedValue }) => (
        <Stack direction="row" alignItems="center" height="100%">
          <Tooltip title={`${t('alert:alertingValue')}: ${formatAlertValue(row)}`} arrow>
            <Typography>{formattedValue}</Typography>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'labels.severity',
      headerName: t('severity'),
      type: 'string',
      width: 100,
      editable: false,
      valueGetter: (_, row) => translateAlertSeverity(row.labels.severity),
      renderCell: ({ row }) => (
        <AlertSeverityChip severity={row.labels.severity} size="small" variant="outlined" />
      )
    },
    {
      field: 'labels.alertname',
      headerName: t('name'),
      type: 'string',
      width: 250,
      editable: false,
      valueGetter: (_, row) => translateAlertRuleName(row.labels.alertname),
      renderCell: ({ row }) => <AlertRuleLabel alertRule={row.labels.alertname} />
    },
    /*{
      field: 'labels.name',
      headerName: t('phase'),
      headerAlign: 'center',
      type: 'string',
      width: 100,
      editable: false,
      align: 'center',
      valueGetter: (_, row) => translateAlertPhase(row)
    },*/
    {
      field: 'summary',
      headerName: t('summary'),
      type: 'string',
      flex: 1,
      editable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
          <AlertSummary
            alert={row}
            sensor={sensor}
            valveSensor={valveSensor}
            ruuviTagCondition={ruuviTagCondition}
            ruuviTagBattery={ruuviTagBattery}
            translationKey={translationKey}
          />
        </Stack>
      )
    },
    {
      field: 'value',
      headerName: t('alert:alertingValue'),
      type: 'number',
      width: 150,
      editable: false,
      valueGetter: (_, alert) =>
        isSensorAlert(alert)
          ? sensor
            ? getAlertValueBySensor(alert, sensor)
            : getAlertValue(alert)
          : isValveSensorAlert(alert)
            ? valveSensor
              ? getAlertValueByValveSensor(alert, valveSensor)
              : getAlertValue(alert)
            : isRuuviTagConditionAlert(alert)
              ? ruuviTagCondition
                ? getAlertValueByRuuviTagCondition(alert, ruuviTagCondition)
                : getAlertValue(alert)
              : isRuuviTagBatteryAlert(alert)
                ? ruuviTagBattery
                  ? getAlertValueByRuuviTagBattery(alert, ruuviTagBattery)
                  : getAlertValue(alert)
                : getAlertValue(alert),

      valueFormatter: (_, alert) =>
        isSensorAlert(alert)
          ? sensor
            ? formatAlertValueBySensor(alert, sensor)
            : formatAlertValue(alert)
          : isValveSensorAlert(alert)
            ? valveSensor
              ? formatAlertValueByValveSensor(alert, valveSensor)
              : formatAlertValue(alert)
            : isRuuviTagConditionAlert(alert)
              ? ruuviTagCondition
                ? formatAlertValueByRuuviTagCondition(alert, ruuviTagCondition)
                : formatAlertValue(alert)
              : isRuuviTagBatteryAlert(alert)
                ? ruuviTagBattery
                  ? formatAlertValueByRuuviTagBattery(alert, ruuviTagBattery)
                  : formatAlertValue(alert)
                : formatAlertValue(alert),
      renderCell: ({ formattedValue }) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          height="100%"
          width="100%"
        >
          {sensor || valveSensor || ruuviTagCondition || ruuviTagBattery ? (
            <BlinkingComponent>
              <Typography color="error" sx={{ cursor: 'pointer' }}>
                {formattedValue}
              </Typography>
            </BlinkingComponent>
          ) : (
            <Typography color="error">{formattedValue}</Typography>
          )}
        </Stack>
      )
    },
    {
      field: 'labels.param1',
      headerName: t('threshold'),
      type: 'number',
      align: 'right',
      width: 150,
      editable: false,
      valueGetter: (_, row) => getAlertThresholdValue(row),
      valueFormatter: (_, row) => formatAlertThresholdValue(row)
    }
    // {
    //   field: 'annotations.summary',
    //   headerName: t('summary'),
    //   type: 'string',
    //   width: 500,
    //   editable: false,
    //   valueGetter: (_, row) => row.annotations.summary
    // }
  ]

  /**
   * The unique alerts by alert name and phase.
   */
  const uniqueAlerts = getUniqueAlerts(alerts)

  /**
   * Get the row ID.
   */
  const handleRowId = (alert: PrometheusAlert) => {
    let rowId = `${alert.labels.device}-${alert.labels.alertname}-${alert.labels.name}`

    if (alert?.startsAt) {
      rowId = `${rowId}-${new Date(alert.startsAt).getTime()}`
    } else if (alert?.activeAt) {
      rowId += `${rowId}-${new Date(alert.activeAt).getTime()}`
    }

    return rowId
  }

  return (
    <Card>
      <CardHeader
        title={t('activeAlerts')}
        action={
          uniqueAlerts.length > 0 && (
            <FormControlLabel
              label={t('alert:showAlertingValuesInSummary')}
              labelPlacement="start"
              sx={{ mr: 2 }}
              control={
                <Switch
                  size="small"
                  checked={translationKey === 'eval'}
                  onChange={(_, checked) => {
                    if (checked) {
                      setTranslationKey('eval')
                    } else {
                      setTranslationKey('summary')
                    }
                  }}
                />
              }
            />
          )
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <LoadingState />
        ) : uniqueAlerts.length === 0 ? (
          <Alert severity="success">{t('noActiveAlerts')}</Alert>
        ) : (
          <DataGrid
            rows={uniqueAlerts}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={loading}
            disableRowSelectionOnClick
            slots={{
              toolbar: GridToolbar
            }}
            slotProps={{
              toolbar: {
                csvOptions: { disableToolbarButton: true },
                printOptions: { disableToolbarButton: true },
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 250 }
              }
            }}
            getRowId={handleRowId}
            sx={{ border: 0 }}
          />
        )}
      </CardContent>
    </Card>
  )
}
